import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useIsAdmin } from '@/hooks/useAdmin'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { AcceptInviteForm } from '@/components/auth/AcceptInviteForm'
import { LoginForm } from '@/components/auth/LoginForm'
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm'
import { ResetPasswordForm } from '@/components/auth/ResetPasswordForm'
import { Overview } from '@/components/dashboard/Overview'
import { ProfileForm } from '@/components/profile/ProfileForm'
import { DownloadsPage } from '@/components/downloads/DownloadsPage'
import { AdminPage } from '@/components/admin/AdminPage'
import { StatsPage } from '@/components/admin/StatsPage'
import { DiscoveryWizard } from '@/components/admin/discovery/DiscoveryWizard'
import { OfficeTVPage } from '@/components/office-tv/OfficeTVPage'
import { OrganizationPage } from '@/components/orgs/OrganizationPage'
import { PostHogProvider } from '@/analytics/PostHogProvider'
import { Loader2 } from 'lucide-react'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()

  if (!user) {
    const next = location.pathname === '/' ? '' : `?next=${encodeURIComponent(location.pathname)}`
    return <Navigate to={`/login${next}`} replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const { isAdmin, isLoading } = useIsAdmin()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  )
}

export default function App() {
  const { loading } = useAuth()

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <PostHogProvider>
      <Routes>
        {/* Public auth routes - each handles its own redirect logic */}
        <Route path="/login" element={<LoginForm />} />
        <Route path="/accept-invite" element={<AcceptInviteForm />} />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        {/* Admin route without DashboardLayout (full-screen TV display) */}
        <Route
          path="/admin/office-tv"
          element={
            <AdminRoute>
              <OfficeTVPage />
            </AdminRoute>
          }
        />

        {/* Protected routes with DashboardLayout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<Overview />} />
                  <Route path="/profile" element={<ProfileForm />} />
                  <Route path="/organization" element={<OrganizationPage />} />
                  <Route path="/downloads" element={<DownloadsPage />} />
                  {/* Admin routes - additionally gated by AdminRoute wrapper in each component */}
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/stats" element={<StatsPage />} />
                  <Route path="/admin/pricing" element={<DiscoveryWizard />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </PostHogProvider>
  )
}
